import {
  UserRole,
  clientDtoSchema,
  contactInfoDtoSchema,
  clientCompanyDetailsSchema,
  PaymentBankOptionEnum,
  BillingFrequency,
  zo,
} from 'remote/global';
import isIBAN from 'validator/es/lib/isIBAN';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { fileTypeObject } from '../types';

const baseAuthenticationSchema = zo.object({
  password: zo.string().min(1).min(8).max(128),
  confirmPassword: zo.string().min(1).min(8).max(128),
  termsAccepted: zo.literal(true),
});

const accountMemberSchema = zo.object({
  id: zo.number(),
  role: zo.nativeEnum(UserRole),
  // We can't extend the default contactInfoSchema because of backwards-compatibility
  contactInfo: zo
    .object({
      fullName: zo.string().optional(),
    })
    .optional(),
});

const accountUserSchema = zo.object({
  email: zo.string().min(1).email(),
  role: zo.nativeEnum(UserRole),
  lang: zo.string().optional(),
});

const accountInvitationSchema = zo.object({
  users: zo.array(accountUserSchema),
});

const accountInvitationRegistrationSchema = baseAuthenticationSchema
  .merge(
    zo.object({
      contactInfo: contactInfoDtoSchema,
      lang: zo.string().optional(),
      uuid: zo.string(),
    })
  )
  .refine((data) => data.password === data.confirmPassword, {
    params: { i18n: 'passwordMismatch' },
    path: ['confirmPassword'],
  });

const maxLenRegCodeByCountry = {
  EE: 8,
  LV: 11,
};
const companyDetailsSchema = clientCompanyDetailsSchema
  .and(
    zo.object({
      operatingLicense: zo.string().min(1).max(16), // Looks something like YTL000735
    })
  )
  .superRefine((data, ctx) => {
    const len = (maxLenRegCodeByCountry as any)[data.country];
    if (len != null && data.regCode.length < len)
      ctx.addIssue({
        code: zo.ZodIssueCode.too_small,
        minimum: len,
        inclusive: true,
        type: 'number',
        path: ['regCode'],
      });
  });

const clientBaseSchema = clientDtoSchema.and(
  zo.object({
    operatingLicense: zo.string().min(1).max(16), // Looks something like YTL000735
  })
);

const companyContactDetailsSchema = zo.object({
  brandName: zo.string().min(1).max(64),
  contactPhoneNumber: zo
    .string()
    .min(1)
    .refine((val) =>
      isValidPhoneNumber(val, {
        defaultCountry: 'EE',
        defaultCallingCode: '372',
      })
    ),
  contactEmail: zo.string().min(1).email(),
  emergencyPhoneNumber: zo
    .string()
    .min(1)
    .refine((val) =>
      isValidPhoneNumber(val, {
        defaultCountry: 'EE',
        defaultCallingCode: '372',
      })
    ),
  emergencyEmail: zo.string().email().optional().or(zo.literal('')),
  website: zo.string().url().optional().or(zo.literal('')),
  offerContactEmail: zo.string().min(1).email().or(zo.literal('')).optional(),
  offerContactPhone: zo
    .string()
    .min(1)
    .refine((val) =>
      isValidPhoneNumber(val, {
        defaultCountry: 'EE',
        defaultCallingCode: '372',
      })
    )
    .or(zo.literal(''))
    .nullable()
    .optional(),
});

const operatorSchema = companyDetailsSchema.and(companyContactDetailsSchema);

const accountCompanyDetailsSchema = zo.intersection(
  operatorSchema,
  zo.object({
    description: zo.string().min(1).or(zo.literal('')),
  })
);

const accountCompanyImageSchema = zo.object({
  attachments: zo.array(fileTypeObject),
});

const authenticationSchema = baseAuthenticationSchema.refine(
  (data) => data.password === data.confirmPassword,
  { params: { i18n: 'passwordMismatch' }, path: ['confirmPassword'] }
);

const baseAuthWithoutTerms = baseAuthenticationSchema.omit({ termsAccepted: true });
const basePasswordSchema = zo
  .intersection(
    baseAuthWithoutTerms,
    zo.object({
      newPassword: zo.string().min(1).min(8).max(128),
    })
  )
  .refine((data) => data.confirmPassword === data.newPassword, {
    params: { i18n: 'passwordMismatch' },
    path: ['confirmPassword'],
  })
  .refine((data) => data.newPassword !== data.password, {
    params: { i18n: 'newPasswordSameAsOld' },
    path: ['newPassword'],
  });

const accountConfigurationSchema = zo.object({});

const accountBankDetailsSchema = zo.object({
  iban: zo
    .string()
    .min(1)
    .refine(isIBAN, { params: { i18n: 'incorrect' } })
    .or(zo.literal('')),
});

const operatorBankAccountDtoSchema = accountBankDetailsSchema.pick({ iban: true }).and(
  zo.object({
    bank: zo.nativeEnum(PaymentBankOptionEnum).optional(),
    bankName: zo.string().optional(),
    swiftCode: zo.string().optional(),
  })
);

const feedbackRequestSchema = zo.object({
  email: zo.string().min(1).email(),
  lang: zo.string().max(3),
  message: zo.string().min(1).max(256).or(zo.literal('')), // literal because we add the message after
  name: zo.string().min(1).max(64),
  phoneNumber: contactInfoDtoSchema.shape.phone,
});

type FeedbackRequestSchemaType = zo.infer<typeof feedbackRequestSchema>;

const operatorAccountPlanSchema = feedbackRequestSchema.and(
  zo.object({
    planId: zo.number(),
    billingFrequency: zo.nativeEnum(BillingFrequency),
  })
);
type OperatorAccountPlanSchemaType = zo.infer<typeof operatorAccountPlanSchema>;

type AccountInvitationRegistrationSchemaType = zo.infer<typeof accountInvitationRegistrationSchema>;
type PasswordSchemaType = zo.infer<typeof basePasswordSchema>;
type AccountCompanyImageSchemaType = zo.infer<typeof accountCompanyImageSchema>;
type AccountCompanyDetailsSchemaType = zo.infer<typeof accountCompanyDetailsSchema>;
type AccountUserSchemaType = zo.infer<typeof accountUserSchema>;
type AccountInvitationSchemaType = zo.infer<typeof accountInvitationSchema>;
type CompanyDetailsSchemaType = zo.infer<typeof companyDetailsSchema>;
type CompanyContactDetailsSchemaType = zo.infer<typeof companyContactDetailsSchema>;
type OperatorSchemaType = zo.infer<typeof operatorSchema>;
type AuthenticationSchemaType = zo.infer<typeof authenticationSchema>;
type AccountMemberSchemaType = zo.infer<typeof accountMemberSchema>;
type AccountConfigurationSchemaType = zo.infer<typeof accountConfigurationSchema>;
type AccountBankDetailsSchemaType = zo.infer<typeof accountBankDetailsSchema>;
type OperatorBankAccountDtoSchemaType = zo.infer<typeof operatorBankAccountDtoSchema>;

export {
  accountCompanyImageSchema,
  accountCompanyDetailsSchema,
  baseAuthenticationSchema,
  basePasswordSchema,
  authenticationSchema,
  companyDetailsSchema,
  companyContactDetailsSchema,
  operatorSchema,
  accountInvitationSchema,
  accountInvitationRegistrationSchema,
  accountMemberSchema,
  clientBaseSchema,
  accountConfigurationSchema,
  accountBankDetailsSchema,
  operatorAccountPlanSchema,
};
export type {
  AccountCompanyImageSchemaType,
  AccountCompanyDetailsSchemaType,
  AuthenticationSchemaType,
  PasswordSchemaType,
  CompanyDetailsSchemaType,
  CompanyContactDetailsSchemaType,
  OperatorSchemaType,
  AccountInvitationSchemaType,
  AccountUserSchemaType,
  AccountInvitationRegistrationSchemaType,
  AccountMemberSchemaType,
  AccountConfigurationSchemaType,
  AccountBankDetailsSchemaType,
  OperatorBankAccountDtoSchemaType,
  OperatorAccountPlanSchemaType,
  FeedbackRequestSchemaType,
};
